import { campaignCopy, selectIsReadOnlyAccess, useCampaignCapabilitiesStore } from '@discovr/features';
import type { BulkApproveItem } from '@discovr/features';
import { fmtDeliverableType } from '@discovr/types';
import { ActionConfirmDialog } from '../../../components/common/ActionConfirmDialog';
import { BulkCreatorLine } from './BulkCreatorLine';
import {
  approvableSelection,
  hasMainSelected,
  variantNeedsDeliverables,
  type BulkApproveVariant,
} from './bulkSelection';
import type { CreatorsRow } from './types';

const ADMIN_APPROVE_TITLE = 'Confirm admin approval?';
const ADMIN_APPROVE_DESCRIPTION =
  'These creators were approved by the brand with the deliverables shown. Confirm to finalize all of them.';

function dialogCopy(variant: BulkApproveVariant, count: number): { title: string; description: string } {
  const copy = campaignCopy.creator.bulkApprove.dialog;
  if (variant === 'brand-approve') return { title: copy.brandApprove.title(count), description: copy.brandApprove.description };
  if (variant === 'approve-as-brand') return { title: copy.approveAsBrand.title(count), description: copy.approveAsBrand.description };
  return { title: ADMIN_APPROVE_TITLE, description: ADMIN_APPROVE_DESCRIPTION };
}

function confirmLabel(count: number): string {
  return `Confirm ${count}`;
}

export interface BulkApproveDialogProps {
  variant: BulkApproveVariant;
  rows: CreatorsRow[];
  platform: string;
  /** Unsaved commercial picks from the table, which win over the persisted ones. */
  optimisticSelections: ReadonlyMap<string, string[]>;
  busy: boolean;
  onClose: () => void;
  /** Drops one creator from the bulk selection. */
  onDeselect: (rowId: string) => void;
  onConfirm: (items: BulkApproveItem[]) => Promise<void>;
}

function brandAgreedTypes(row: CreatorsRow): string[] {
  return row.deliverables.filter((d) => d.is_selected).map((d) => d.type);
}

/**
 * Confirm step for a bulk approval. On Pending (brand, or admin on behalf of
 * the brand) each card shows the commercial picked in the table and that
 * selection is sent. Admin final approval shows the deliverables the brand
 * agreed to, locked, and sends only the entry ids. Confirm needs a main on
 * every card, as the single-row dialogs do.
 */
export function BulkApproveDialog({
  variant,
  rows,
  platform,
  optimisticSelections,
  busy,
  onClose,
  onDeselect,
  onConfirm,
}: BulkApproveDialogProps) {
  const isReadOnly = useCampaignCapabilitiesStore(selectIsReadOnlyAccess);
  const sendsDeliverables = variantNeedsDeliverables(variant);
  const typesFor = (row: CreatorsRow): string[] =>
    sendsDeliverables ? approvableSelection(row, optimisticSelections.get(String(row.id))) : brandAgreedTypes(row);
  const withoutMainCount = rows.filter((row) => !hasMainSelected(typesFor(row), platform)).length;
  const canConfirm = !isReadOnly && rows.length > 0 && withoutMainCount === 0;
  const { title, description } = dialogCopy(variant, rows.length);

  const handleConfirm = async () => {
    await onConfirm(
      rows.map((row) => {
        const entry_id = String(row.id);
        if (!sendsDeliverables) return { entry_id };
        return { entry_id, selected_deliverables: typesFor(row) };
      }),
    );
  };

  // Cards stay removable: a row can go stale under the open dialog (approved
  // elsewhere, or hidden by a row action), and the selection is the only place
  // left to drop it.
  const extraContent = (
    <div className="space-y-4">
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={String(row.id)} className="space-y-2 rounded-lg border border-border p-3">
            <BulkCreatorLine row={row} platform={platform} />
            <SelectionChips types={typesFor(row)} />
            {!isReadOnly && (
              <button
                type="button"
                className="text-xs font-medium text-destructive hover:underline"
                onClick={() => onDeselect(String(row.id))}
                disabled={busy}
              >
                Remove from selection
              </button>
            )}
          </li>
        ))}
      </ul>
      {withoutMainCount > 0 && (
        <p role="alert" className="text-xs font-medium text-destructive">
          {campaignCopy.creator.bulkApprove.dialog.missingMain(withoutMainCount)}
        </p>
      )}
      {isReadOnly && (
        <p className="text-[10px] font-medium text-muted-foreground">
          View-only — you do not have access to change or confirm this selection.
        </p>
      )}
    </div>
  );

  return (
    <ActionConfirmDialog
      open
      onOpenChange={(open) => !open && onClose()}
      variant={variant}
      title={title}
      description={description}
      confirmLabel={confirmLabel(rows.length)}
      extraContent={extraContent}
      canConfirm={canConfirm}
      busy={busy}
      onConfirm={handleConfirm}
    />
  );
}

function SelectionChips({ types }: { types: string[] }) {
  if (types.length === 0) {
    return <p className="text-xs text-muted-foreground">No deliverable selected</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {types.map((t) => (
        <span key={t} className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-semibold text-foreground">
          {fmtDeliverableType(t)}
        </span>
      ))}
    </div>
  );
}
